/**
 * API 请求工具类
 * 统一管理 platform_center 和 mvme_service 的 API 调用
 * v3.0 - 适配多平台架构，使用 platform_code 参数
 */

// API 基础配置
const PLATFORM_BASE_URL = 'https://platform.taihestellar.com';
const MVME_BASE_URL = 'https://mvme.taihestellar.com';

// 平台代码配置 - web_global 对应的 platform_code
const PLATFORM_CODE = 'web_global';

// 存储键名
const STORAGE_KEYS = {
  TOKEN: 'mvme_token',
  USER: 'mvme_user'
};

/**
 * 根据 endpoint 决定使用哪个服务器
 * @param {string} endpoint - API 端点路径
 * @returns {string} 完整的 API URL
 */
function getApiUrl(endpoint) {
  // 用户认证相关 → platform_center
  if (endpoint.startsWith('/api/v1/auth/')) {
    return PLATFORM_BASE_URL;
  }
  // 用户相关 → platform_center
  if (endpoint.startsWith('/api/v1/user/')) {
    return PLATFORM_BASE_URL;
  }
  // 积分相关 → platform_center
  if (endpoint.startsWith('/api/v1/point/')) {
    return PLATFORM_BASE_URL;
  }
  // 订单相关 → platform_center
  if (endpoint.startsWith('/api/v1/order/')) {
    return PLATFORM_BASE_URL;
  }
  // 视频生成相关 → mvme_service
  if (endpoint.startsWith('/api/v1/create') ||
      endpoint.startsWith('/api/v1/get_progress') ||
      endpoint.startsWith('/api/v1/get_works') ||
      endpoint.startsWith('/api/v1/result/')) {
    return MVME_BASE_URL;
  }
  // 默认返回 mvme_service
  return MVME_BASE_URL;
}

/**
 * 统一 API 请求方法
 * @param {string} endpoint - API 端点
 * @param {object} options - 请求选项
 * @returns {Promise} API 响应
 */
export async function apiRequest(endpoint, options = {}) {
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  const userId = localStorage.getItem('user_id');  // 系统统一ID
  const appSpecificId = localStorage.getItem('app_specific_id');  // 应用特有ID
  const baseUrl = getApiUrl(endpoint);
  const isPlatformCenter = baseUrl === PLATFORM_BASE_URL;

  // 构建请求头
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...(userId && { 'X-User-Id': userId }),
    ...(appSpecificId && { 'X-App-Id': appSpecificId }),
    ...options.headers
  };

  // 处理请求体
  let body = options.body;
  let url = `${baseUrl}${endpoint}`;

  // v3.0新增：为 platform_center 的 API 自动添加 platform_code 参数
  if (isPlatformCenter && body && !(body instanceof FormData)) {
    // JSON 请求：将 platform_code 添加到请求体
    body = {
      ...body,
      platform_code: PLATFORM_CODE
    };
  } else if (isPlatformCenter && (!body || body instanceof FormData)) {
    // GET 请求或 FormData：将 platform_code 添加到 URL 查询参数
    const separator = endpoint.includes('?') ? '&' : '?';
    url = `${baseUrl}${endpoint}${separator}platform_code=${PLATFORM_CODE}`;
  }

  // 处理特殊请求体（FormData）
  if (body && !(body instanceof FormData)) {
    body = JSON.stringify(body);
  }

  const response = await fetch(url, {
    ...options,
    headers,
    body
  });

  // Token 过期处理
  if (response.status === 401) {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem('user_id');
    localStorage.removeItem('app_specific_id');
    // 触发页面重新加载以重置状态
    window.dispatchEvent(new Event('auth-expired'));
  }

  // 解析响应
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return await response.json();
  }
  return await response.text();
}

/**
 * 文件上传方法（用于视频生成）
 * @param {string} endpoint - API 端点
 * @param {FormData} formData - 表单数据
 * @returns {Promise} API 响应
 */
export async function uploadFile(endpoint, formData) {
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  const userId = localStorage.getItem('user_id');  // 系统统一ID
  const appSpecificId = localStorage.getItem('app_specific_id');  // 应用特有ID
  const baseUrl = MVME_BASE_URL;

  const headers = {
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...(userId && { 'X-User-Id': userId }),
    ...(appSpecificId && { 'X-App-Id': appSpecificId })
  };

  const response = await fetch(`${baseUrl}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData
  });

  // Token 过期处理
  if (response.status === 401) {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem('user_id');
    localStorage.removeItem('app_specific_id');
    window.dispatchEvent(new Event('auth-expired'));
  }

  return await response.json();
}

// ============ 认证相关 API ============

/**
 * 邮箱登录
 * @param {string} email - 邮箱
 * @param {string} password - 密码
 * @returns {Promise} 登录响应
 */
export async function loginWithEmail(email, password) {
  return apiRequest('/api/v1/auth/login/email', {
    method: 'POST',
    body: {
      email,
      password,
      platform_type: 'web_global'
    }
  });
}

/**
 * 邮箱注册
 * @param {string} email - 邮箱
 * @param {string} password - 密码
 * @param {string} name - 用户名
 * @returns {Promise} 注册响应
 */
export async function registerWithEmail(email, password, name) {
  return apiRequest('/api/v1/auth/register/email', {
    method: 'POST',
    body: {
      email,
      password,
      name,
      platform_type: 'web_global'
    }
  });
}

/**
 * Google 登录
 * @param {string} idToken - Google ID Token
 * @returns {Promise} 登录响应
 */
export async function loginWithGoogle(idToken) {
  return apiRequest('/api/v1/auth/login/google', {
    method: 'POST',
    body: {
      id_token: idToken,
      platform_type: 'web_global'
    }
  });
}

/**
 * 获取当前用户信息
 * @returns {Promise} 用户信息
 */
export async function getCurrentUser() {
  return apiRequest('/api/v1/auth/user/me');
}

// ============ 密码重置 API ============

/**
 * 请求重置密码 - 发送重置邮件
 * @param {string} email - 用户邮箱
 * @returns {Promise} 响应
 */
export async function requestPasswordReset(email) {
  return apiRequest('/api/v1/auth/password/reset-request', {
    method: 'POST',
    body: { email }
  });
}

/**
 * 验证重置令牌
 * @param {string} email - 用户邮箱
 * @param {string} token - 重置令牌
 * @returns {Promise} 响应
 */
export async function verifyResetToken(email, token) {
  return apiRequest('/api/v1/auth/password/verify-token', {
    method: 'POST',
    body: { email, token }
  });
}

/**
 * 使用令牌重置密码
 * @param {string} email - 用户邮箱
 * @param {string} token - 重置令牌
 * @param {string} newPassword - 新密码
 * @returns {Promise} 响应
 */
export async function resetPassword(email, token, newPassword) {
  return apiRequest('/api/v1/auth/password/reset', {
    method: 'POST',
    body: {
      email,
      token,
      new_password: newPassword
    }
  });
}

// ============ 积分相关 API ============

/**
 * 获取用户积分
 * @returns {Promise} 积分信息
 */
export async function getUserPoints() {
  return apiRequest('/api/v1/user/points');
}

/**
 * 获取积分套餐列表
 * @returns {Promise} 套餐列表
 */
export async function getPointPackages() {
  return apiRequest('/api/v1/point/packages');
}

/**
 * 获取视频生成积分配置
 * @returns {Promise} 配置列表
 */
export async function getVideoGenerationConfigs() {
  return apiRequest('/api/v1/point/video-generation-configs');
}

/**
 * 创建充值订单
 * @param {string} packageId - 套餐 ID
 * @param {string} paymentMethod - 支付方式 (paypal/stripe)
 * @returns {Promise} 订单信息
 */
export async function createRechargeOrder(packageId, paymentMethod) {
  return apiRequest('/api/v1/point/recharge', {
    method: 'POST',
    body: {
      package_id: packageId,
      payment_method: paymentMethod
    }
  });
}

// ============ 订单相关 API ============

/**
 * 获取订单列表
 * @param {object} params - 查询参数
 * @returns {Promise} 订单列表
 */
export async function getOrderList(params = {}) {
  const queryString = new URLSearchParams(params).toString();
  return apiRequest(`/api/v1/order/list?${queryString}`);
}

/**
 * 获取订单详情
 * @param {string} orderId - 订单 ID
 * @returns {Promise} 订单详情
 */
export async function getOrderDetail(orderId) {
  return apiRequest(`/api/v1/order/detail/${orderId}`);
}

/**
 * 取消订单
 * @param {string} orderId - 订单 ID
 * @returns {Promise} 操作结果
 */
export async function cancelOrder(orderId) {
  return apiRequest(`/api/v1/order/cancel/${orderId}`, {
    method: 'POST'
  });
}

// ============ 视频生成相关 API ============

/**
 * 创建视频生成任务
 * @param {FormData} formData - 包含图片、标题、主题等的表单数据
 * @returns {Promise} 任务创建结果
 */
export async function createVideoTask(formData) {
  return uploadFile('/api/v1/create', formData);
}

/**
 * 查询任务进度
 * @param {string} taskId - 任务 ID
 * @param {string} userId - 用户 ID
 * @returns {Promise} 任务进度
 */
export async function getTaskProgress(taskId, userId) {
  const formData = new FormData();
  formData.append('task_id', taskId);
  formData.append('user_id', userId);

  return uploadFile('/api/v1/get_progress', formData);
}

/**
 * 获取任务结果
 * @param {string} taskId - 任务 ID
 * @returns {Promise} 任务结果
 */
export async function getTaskResult(taskId) {
  return apiRequest(`/api/v1/result/${taskId}`);
}

/**
 * 获取用户作品列表
 * @param {number|string} userId - 用户 ID（系统统一ID）
 * @returns {Promise} 作品列表
 */
export async function getUserWorks(userId) {
  console.log('📚 getUserWorks called with userId:', userId, 'Type:', typeof userId);

  if (!userId) {
    console.error('❌ getUserWorks: userId is empty');
    return { code: 401, message: '用户ID不能为空', data: [] };
  }

  const formData = new FormData();
  // 确保转换为数字
  formData.append('user_id', parseInt(userId));

  console.log('📚 Sending FormData with user_id:', parseInt(userId));
  return uploadFile('/api/v1/get_works', formData);
}

export default {
  // 认证
  loginWithEmail,
  registerWithEmail,
  loginWithGoogle,
  getCurrentUser,

  // 积分
  getUserPoints,
  getPointPackages,
  getVideoGenerationConfigs,
  createRechargeOrder,

  // 订单
  getOrderList,
  getOrderDetail,
  cancelOrder,

  // 视频生成
  createVideoTask,
  getTaskProgress,
  getTaskResult,
  getUserWorks,

  // 工具函数
  apiRequest,
  uploadFile
};
